import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, EmptyState, Icon, PageHeader } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, API_BASE_URL, isDemoMode } from "../api/client";
import { notifyDataChanged, useCourses } from "../hooks/useCourses";

interface LibraryItem {
  id: string;
  name: string;
  courseId?: string | null;
  course?: string | null;
  status: string;
  size: string;
  type: string;
}

export function LibraryPage() {
  const { t, formatDate, locale, formatNumber } = useI18n();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  // Two-tap inline delete (company law: no window.confirm).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef=useRef<HTMLInputElement>(null);
  const {courses}=useCourses();
  const [courseFilter,setCourseFilter]=useState("all");
  const [uploadCourse,setUploaCourse]=useState("");
  const removeResource = async (resource: LibraryItem) => {
    if (!isDemoMode()) {
      try {
        await api(`/resources/${resource.id}`, { method: "DELETE" });
        notifyDataChanged();
      } catch {
        return;
      }
    }
    setItems((current) => current.filter((item) => item.id !== resource.id));
    setConfirmDeleteId(null);
  };
  useEffect(()=>{if(isDemoMode())return;api<any[]>("/resources").then(data=>setItems(data.map(item=>({id:item.id,name:item.title,courseId:item.courseId??null,course:item.courseTitle??null,status:item.status,size:`${formatNumber(Math.max(1,Math.round(item.sizeBytes/1024)))} KB`,type:item.kind})))).catch(()=>undefined);},[]);
  // Processing files refresh their status automatically.
  useEffect(()=>{
    if(!items.some(item=>item.status!=="ready"&&item.status!=="failed")||isDemoMode())return;
    const timer=window.setTimeout(async()=>{
      try{
        const data=await api<any[]>("/resources");
        setItems(data.map(item=>({id:item.id,name:item.title,courseId:item.courseId??null,course:item.courseTitle??null,status:item.status,size:`${formatNumber(Math.max(1,Math.round(item.sizeBytes/1024)))} KB`,type:item.kind})));
      }catch{/* keep current list */}
    },4000);
    return ()=>window.clearTimeout(timer);
  },[items]);
  const visible=useMemo(()=>items.filter(item=>courseFilter==="all"||(courseFilter==="none"?!item.courseId:item.courseId===courseFilter)),[items,courseFilter]);
  const courseById=(id:string)=>courses.find(course=>course.id===id);
  const upload=async(file:File,targetCourseId:string)=>{
    const targetTitle=targetCourseId?courseById(targetCourseId)?.title||null:null;
    if(isDemoMode()){setItems(current=>[{id:crypto.randomUUID(),name:file.name,courseId:targetCourseId||null,course:targetTitle,status:"processing",size:`${formatNumber(Math.max(1,Math.round(file.size/1024)))} KB`,type:(file.name.split(".").pop()||"file").toLocaleUpperCase(locale)},...current]);return;}
    setUploading(true);
    setUploadError("");
    try{
      const form=new FormData();form.append("file",file);form.append("title",file.name);if(targetCourseId)form.append("courseId",targetCourseId);
      const created=await api<any>("/resources",{method:"POST",body:form});
      setItems(current=>[{id:created.id,name:created.title,courseId:created.courseId??null,course:created.courseTitle??targetTitle,status:created.status,size:`${Math.max(1,Math.round(created.sizeBytes/1024))} KB`,type:created.kind},...current]);
    }catch(reason){
      setUploadError(reason instanceof Error&&reason.message?`${t("library.uploadError")}: ${reason.message}`:t("library.uploadError"));
    }finally{
      setUploading(false);
    }
  };
  return (
    <div className="page-stack">
      <PageHeader title={t("library.title")} subtitle={t("library.empty")} actions={<>
        <select aria-label={t("library.course")} value={uploadCourse} onChange={(event)=>setUploaCourse(event.target.value)} className="library-upload-select">
          <option value="">{t("library.general")}</option>
          {courses.map(course=><option key={course.id} value={course.id}>{course.title}</option>)}
        </select>
        <input ref={inputRef} type="file" hidden onChange={async(event)=>{const file=event.target.files?.[0];if(!file)return;await upload(file,uploadCourse);if(inputRef.current)inputRef.current.value="";}}/>
        <Button icon="upload" loading={uploading} onClick={()=>inputRef.current?.click()}>{t("library.upload")}</Button>
      </>} />
      {uploadError && <div className="form-error" role="alert">{uploadError}</div>}
      <div className="library-filter">
        <span>{t("library.course")}</span>
        <select value={courseFilter} onChange={(event)=>setCourseFilter(event.target.value)} aria-label={t("library.allCourses")}>
          <option value="all">{t("library.allCourses")}</option>
          <option value="none">{t("library.general")}</option>
          {courses.map(course=><option key={course.id} value={course.id}>{course.title}</option>)}
        </select>
      </div>
      <Card className="resource-table">
        <div className="resource-table__header"><span>{t("library.name")}</span><span>{t("library.course")}</span><span>{t("admin.status")}</span><span /></div>
        {visible.map((resource) => (
          <article className="resource-row" key={resource.id}>
            <span className="resource-row__file"><i><Icon name="file" /></i><div>
              {renaming === resource.id ? (
                <form className="resource-rename" onSubmit={async (event) => { event.preventDefault(); const title = renameValue.trim(); if (!title) { setRenaming(null); return; } if (!isDemoMode()) { try { await api(`/resources/${resource.id}`, { method: "PATCH", body: JSON.stringify({ title }) }); } catch { setRenaming(null); return; } } setItems((current) => current.map((item) => item.id === resource.id ? { ...item, name: title } : item)); setRenaming(null); }}>
                  <input autoFocus value={renameValue} onChange={(event) => setRenameValue(event.target.value)} onBlur={(event) => { if ((event.relatedTarget as HTMLElement)?.closest("form")) return; setRenaming(null); }} />
                  <button type="submit"><Icon name="check" size={14} /></button>
                </form>
              ) : (
                <>
                  <strong>{resource.name}</strong>
                  <small>{resource.type} · {resource.size}</small>
                </>
              )}
            </div></span>
            <span>{resource.courseId && resource.course ? <span className="resource-course-chip"><i />{resource.course}</span> : <span className="resource-course-chip" style={{ background: "#f1f2f8", color: "#6f7285" }}>{t("library.general")}</span>}</span>
            <span><Badge tone={resource.status === "ready" ? "green" : resource.status === "failed" ? "coral" : "amber"}>{t(resource.status === "ready" ? "library.ready" : resource.status === "failed" ? "common.error" : "library.processing")}</Badge></span>
            <span className="resource-row__actions">
              <button title={t("common.edit")} onClick={() => { setRenaming(resource.id); setRenameValue(resource.name); }}><Icon name="edit" size={15} /></button>
              {confirmDeleteId === resource.id ? (
                <>
                  <button className="is-danger" title={t("common.delete")} onClick={() => void removeResource(resource)} aria-label={t("common.delete")}><Icon name="check" size={15} /></button>
                  <button title={t("common.cancel")} onClick={() => setConfirmDeleteId(null)} aria-label={t("common.cancel")}><Icon name="x" size={15} /></button>
                </>
              ) : (
                <button title={t("common.delete")} onClick={() => setConfirmDeleteId(resource.id)} aria-label={t("common.delete")}><Icon name="trash" size={15} /></button>
              )}
              <a href={`${API_BASE_URL}/resources/${resource.id}/content`} target="_blank" rel="noreferrer" aria-label={t("library.download")}><Icon name="arrow" /></a>
            </span>
          </article>
        ))}
        {!visible.length && <EmptyState icon="library" title={t("library.empty")} />}
      </Card>
    </div>
  );
}
